import React from "react";
import moment from "moment-timezone";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faCheckCircle } from "@fortawesome/pro-solid-svg-icons";

import Grid from "components/Grid";
import Loading from "components/Loading";
import Panel from "components/Panel";
import { formatTimestampShortest } from "utils/format";

import { useCollectorStatusQuery } from "./gql/Query.generated";

type Props = {
  serverId: string;
};

const Status = ({ serverId }: Props) => {
  const { data, loading, error } = useCollectorStatusQuery({
    variables: { serverId },
  });
  if (loading || error) {
    return <Loading error={!!error} />;
  }
  return (
    <>
      <Panel title="Recent Errors">
        <Grid
          noRowsText={
            <>
              No recent errors{" "}
              <FontAwesomeIcon
                className="text-[#43962A]"
                icon={faCheckCircle}
              />
            </>
          }
          data={data.errors}
          defaultSortBy="collectedAt"
          pageSize={10}
          columns={[
            {
              field: "kind",
              width: "8%",
            },
            {
              field: "collectedAt",
              header: "Collected At",
              defaultSortOrder: "DESC",
              width: "14%",
              renderer: function ({ fieldData }) {
                return formatTimestampShortest(moment.unix(fieldData));
              },
            },
            {
              field: "collectorErrors",
              header: "Collector Error",
              tip: "Error that happened while the collector was capturing data",
              width: "39%",
              renderer: function ({ fieldData }) {
                return (
                  <div className="whitespace-pre-wrap">
                    {fieldData?.join("\n")}
                  </div>
                );
              },
            },
            {
              field: "snapshotErrors",
              header: "Processing Error",
              tip: "Error that happened while processing data received from the collector",
              width: "39%",
              renderer: function ({ fieldData }) {
                return (
                  <div className="whitespace-pre-wrap">
                    {fieldData?.join("\n")}
                  </div>
                );
              },
            },
          ]}
        />
      </Panel>
      <Panel title="Recent Connections">
        <Grid
          noRowsText="The collector has not recently connected to pganalyze. Please check that the collector is still active and its logs show normal activity."
          data={data.connections}
          defaultSortBy="createdAt"
          pageSize={10}
          columns={[
            {
              field: "status",
              width: "8%",
            },
            {
              field: "age",
              header: "Age",
              tip: "Amount of time the connection is/was active. Short connection ages suggest network reliability issues or collector crashes.",
              width: "10%",
              renderer: function ({ fieldData }) {
                return (
                  fieldData && moment.duration(fieldData, "second").humanize()
                );
              },
            },
            {
              field: "createdAt",
              header: "Created At",
              tip: "Time that the connection was first established",
              defaultSortOrder: "DESC",
              width: "14%",
              renderer: function ({ fieldData }) {
                return formatTimestampShortest(moment.unix(fieldData));
              },
            },
            {
              field: "stoppedAt",
              header: "Stopped At",
              tip: "Time when the connection ended",
              width: "14%",
              renderer: function ({ fieldData }) {
                return (
                  fieldData && formatTimestampShortest(moment.unix(fieldData))
                );
              },
            },
            {
              field: "pausedAt",
              header: "Paused At",
              tip: "When multiple collectors monitor the same server (during a staged deploy or with a high-availability collector setup), pganalyze pauses collection from all but one.",
              width: "14%",
              renderer: function ({ fieldData }) {
                return (
                  fieldData && formatTimestampShortest(moment.unix(fieldData))
                );
              },
            },
            {
              field: "snapshots",
              tip: "Total number of snapshots submitted over this connection",
              width: "12%",
            },
            {
              field: "ipAddress",
              header: "IP Address",
              width: "20%",
            },
            {
              field: "version",
              width: "8%",
            },
          ]}
        />
      </Panel>
    </>
  );
};

export default Status;
